export type HeroCta = {
  label: string;
  href: string;
};

export type HeroContent = {
  badge: string;
  title: string;
  subtitle: string;
  primaryCta: HeroCta;
  secondaryCta: HeroCta;
  trustChips: string[];
};

export type HomeProduct = {
  id: string;
  title: string;
  category: string;
  price: number;
  image: string;
  inStock: boolean;
  stockLabel: string;
  highlight: string;
  href: string;
};

export type HomeCategory = {
  id: string;
  label: string;
  href: string;
  count: number;
  description: string;
};

export type DealHighlight = {
  id: string;
  title: string;
  copy: string;
  metric: string;
  href: string;
};

export type TrustIcon = "shield" | "truck" | "leaf" | "support";

export type TrustItem = {
  id: string;
  icon: TrustIcon;
  title: string;
  copy: string;
  stat: string;
};

export type ReviewEntry = {
  id: string;
  name: string;
  city: string;
  rating: number;
  product: string;
  quote: string;
  date: string;
  verified: boolean;
};

export const heroContent: HeroContent = {
  badge: "Neu eingetroffen",
  title: "Premium Zubehör für deinen modernen Alltag.",
  subtitle:
    "Entdecke sorgfältig ausgewähltes Raucherzubehör, Aufbewahrung und Pflegeprodukte mit Fokus auf Qualität, Design und Haltbarkeit.",
  primaryCta: {
    label: "Jetzt entdecken",
    href: "/produkte?sort=newest",
  },
  secondaryCta: {
    label: "Highlights ansehen",
    href: "/produkte?badge=sale",
  },
  trustChips: [
    "Diskreter Versand in DE",
    "Sicher bezahlen",
    "Versand in 1-3 Werktagen",
  ],
};

export const homeProducts: HomeProduct[] = [
  {
    id: "grinder-titancut-s",
    title: "TitanCut Grinder S",
    category: "Grinder",
    price: 24.9,
    image: "/products/grinder/alu-small.jpg",
    inStock: true,
    stockLabel: "Auf Lager",
    highlight: "Neu",
    href: "/produkte?search=grinder",
  },
  {
    id: "grinder-titancut-l",
    title: "TitanCut Grinder L",
    category: "Grinder",
    price: 31.9,
    image: "/products/grinder/alu-large.jpg",
    inStock: true,
    stockLabel: "Auf Lager",
    highlight: "Bestseller",
    href: "/produkte?search=grinder",
  },
  {
    id: "eg-one-case",
    title: "EG One Hardcase",
    category: "Aufbewahrung",
    price: 39.9,
    image: "/products/eg-one/front.jpg",
    inStock: true,
    stockLabel: "Auf Lager",
    highlight: "Neu",
    href: "/produkte?search=case",
  },
  {
    id: "jar-matte",
    title: "Aroma Glas Jar M",
    category: "Storage",
    price: 12.9,
    image: "/products/jar/m.jpg",
    inStock: true,
    stockLabel: "Auf Lager",
    highlight: "Top bewertet",
    href: "/produkte?search=jar",
  },
  {
    id: "paper-classic",
    title: "Classic Papers King Size",
    category: "Papers",
    price: 3.9,
    image: "/products/papers/classic.jpg",
    inStock: true,
    stockLabel: "Auf Lager",
    highlight: "Bundle Deal",
    href: "/produkte?search=papers",
  },
  {
    id: "tips-pack",
    title: "Filter Tips Pro Pack",
    category: "Tips",
    price: 4.5,
    image: "/products/filters/tips.jpg",
    inStock: true,
    stockLabel: "Auf Lager",
    highlight: "Neu",
    href: "/produkte?search=tips",
  },
  {
    id: "scale-precision",
    title: "Pocket Scale Precision",
    category: "Waagen",
    price: 27.5,
    image: "/products/scale/precision.jpg",
    inStock: false,
    stockLabel: "Bald wieder da",
    highlight: "Vorbestellung",
    href: "/produkte?search=scale",
  },
  {
    id: "cleaner-pipe",
    title: "Pipe Cleaner Kit",
    category: "Pflege",
    price: 8.9,
    image: "/products/cleaner/pipe.jpg",
    inStock: true,
    stockLabel: "Auf Lager",
    highlight: "Deal",
    href: "/produkte?search=cleaner",
  },
  {
    id: "hardcase-black",
    title: "Hardcase Black Edition",
    category: "Reise",
    price: 34.9,
    image: "/products/case/hard.jpg",
    inStock: true,
    stockLabel: "Auf Lager",
    highlight: "Limitierte Farbe",
    href: "/produkte?search=hard",
  },
];

export const homeCategories: HomeCategory[] = [
  {
    id: "cat-grinder",
    label: "Grinder",
    href: "/produkte?category=grinder",
    count: 24,
    description: "Präzise Mahlung in robusten Materialien.",
  },
  {
    id: "cat-storage",
    label: "Aufbewahrung",
    href: "/produkte?category=storage",
    count: 19,
    description: "Geruchsdicht, stabil und reisefreundlich.",
  },
  {
    id: "cat-papers",
    label: "Papers & Tips",
    href: "/produkte?category=papers",
    count: 31,
    description: "Feine Blättchen und hochwertige Tips.",
  },
  {
    id: "cat-vaporizer",
    label: "Vaporizer Zubehör",
    href: "/produkte?category=vaporizer",
    count: 15,
    description: "Mundstücke, Siebe und Reinigungssets.",
  },
  {
    id: "cat-cleaning",
    label: "Reinigung",
    href: "/produkte?category=cleaning",
    count: 18,
    description: "Sauberkeit für gleichbleibenden Geschmack.",
  },
  {
    id: "cat-scales",
    label: "Waagen",
    href: "/produkte?category=scales",
    count: 12,
    description: "Kompakte Modelle mit hoher Genauigkeit.",
  },
];

export const dealHighlights: DealHighlight[] = [
  {
    id: "deal-bundle-grinder",
    title: "Bundle: Grinder + Jar",
    copy: "Spare beim Kombi-Set für Vorbereitung und Aufbewahrung.",
    metric: "Bis zu 18% sparen",
    href: "/produkte?badge=sale",
  },
  {
    id: "deal-clean-set",
    title: "Pflege Starter Set",
    copy: "Alles für die regelmäßige Reinigung in einem Paket.",
    metric: "Gratis Versand ab 39 EUR",
    href: "/produkte?category=cleaning",
  },
  {
    id: "deal-weekdrop",
    title: "Weekly Drops",
    copy: "Jede Woche neue Farben und limitierte Varianten.",
    metric: "Jeden Freitag um 18:00",
    href: "/produkte?sort=newest",
  },
];

export const trustItems: TrustItem[] = [
  {
    id: "trust-1",
    icon: "shield",
    title: "Geprüfte Qualität",
    copy: "Nur ausgewählte Produkte mit belastbaren Materialien.",
    stat: "98% positive Produktbewertungen",
  },
  {
    id: "trust-2",
    icon: "truck",
    title: "Schneller Versand",
    copy: "Bestellungen bis 14:00 gehen meist am selben Tag raus.",
    stat: "1-3 Werktage Lieferzeit",
  },
  {
    id: "trust-3",
    icon: "leaf",
    title: "Bewusst verpackt",
    copy: "Versandkartons aus Recyclingmaterial und wenig Kunststoff.",
    stat: "90% plastikreduziert",
  },
  {
    id: "trust-4",
    icon: "support",
    title: "Service aus Deutschland",
    copy: "Schnelle Antworten per Mail und persönliche Beratung.",
    stat: "Antwortzeit unter 24h",
  },
];

export const customerReviews: ReviewEntry[] = [
  {
    id: "review-google-1",
    name: "ostgewitter",
    city: "Echt Grandios · Gummersbach",
    rating: 5,
    product: "Rezension aus Google",
    quote:
      "Sehr guter Shop.Mit einer breiten Auswahl und sehr kompetente Personal bin ich immer zufrieden gewesen.",
    date: "vor 4 Monaten",
    verified: false,
  },
  {
    id: "review-google-2",
    name: "Jens Meisel",
    city: "Echt Grandios · Gummersbach",
    rating: 5,
    product: "Rezension aus Google",
    quote:
      "Top Geschäft, sehr schön eingerichtet top Sortiment. Mann kann dort auch qualitativ hochwertige Grills inklusive Zubehör kaufen. Ich bin sehr zufrieden und kann das Geschäft nur empfehlen. Der Inhaber ist sehr freundlich und kompetent.",
    date: "vor 7 Monaten",
    verified: false,
  },
  {
    id: "review-google-3",
    name: "Rudolf Weiss",
    city: "Echt Grandios · Gummersbach",
    rating: 5,
    product: "Rezension aus Google",
    quote:
      "Top Adresse. 1a Ausstattung, alles was man braucht, gute Preise und super freundliche Beratung. Prädikat: absolut empfehlenswert",
    date: "vor einem Jahr",
    verified: false,
  },
  {
    id: "review-google-4",
    name: "John Kremenzow",
    city: "Echt Grandios · Gummersbach",
    rating: 5,
    product: "Rezension aus Google",
    quote:
      "Alles in allem TOP!!! Extrem freundlicher Empfang, sowie Beratung, es wird sich Zeit für den Kunden genommen, die Preise sind auch top & dazu eine Riesenauswahl an Raucherutensilien, teils sogar auch günstiger als online ;) Immer gerne wieder! 👍✌️",
    date: "vor einem Jahr",
    verified: false,
  },
  {
    id: "review-google-5",
    name: "Tizian Laut",
    city: "Echt Grandios · Gummersbach",
    rating: 5,
    product: "Rezension aus Google",
    quote:
      "Dieser Laden ist mein absoluter Safe Space. Mega netter Verkäufer der immer hilft wo er kann, und nie einmal unfreundlich erlebt. Absolute 10/10",
    date: "vor einem Jahr",
    verified: false,
  },
];

export const newsletterBenefits: string[] = [
  "Neue Drops und limitierte Farben zuerst sehen",
  "Exklusive Gutscheine für Abonnenten",
  "Pflege- und Setup-Tipps direkt ins Postfach",
];
